if (!customElements.get('quick-add-modal')) {
    customElements.define(
      'quick-add-modal',
      class QuickAddModal extends ModalDialog {
        constructor() {
          super();
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        }
  
        connectedCallback() {
          if (!this.moved && this.classList.contains('canvoil-collection-quick-add')) {
            document.querySelectorAll('quick-add-modal.canvoil-collection-quick-add').forEach(modal => {
              if (modal !== this && modal.id === this.id) {
                modal.compactRequest?.abort();
                modal.variantRequest?.abort();
                if (modal.hasAttribute('open')) modal.hide();
                modal.remove();
              }
            });
          }
          super.connectedCallback();
        }

        hide(preventFocus = false) {
          this.compactRequest?.abort();
          this.variantRequest?.abort();
          this.compactVersion = (this.compactVersion || 0) + 1;
          if (this.classList.contains('canvoil-collection-quick-add') && this.openedBy) {
            this.openedBy.removeAttribute('aria-disabled');
            this.openedBy.classList.remove('loading');
            this.openedBy.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          }
          const cartNotification = document.querySelector('cart-drawer');
          if (cartNotification) cartNotification.setActiveElement(this.openedBy);
          this.modalContent.innerHTML = '';
  
          if (preventFocus) this.openedBy = null;
          super.hide();
        }
  
        show(opener) {
          if (this.classList.contains('canvoil-collection-quick-add')) return this.showCompact(opener);
          opener.setAttribute('aria-disabled', true);
          opener.classList.add('loading');
          opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');
  
          fetch(opener.getAttribute('data-product-url'))
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
              this.preventDuplicatedIDs();
              this.removeDOMElements();
              this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
  
              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
  
              if (window.ProductModel) window.ProductModel.loadShopifyXR();
  
              this.removeGalleryListSemantic();
              this.updateImageSizes();
              this.preventVariantURLSwitching();
              super.show(opener);
            })
            .finally(() => {
              opener.removeAttribute('aria-disabled');
              opener.classList.remove('loading');
              opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
            });
        }
  
        async showCompact(opener) {
          this.compactRequest?.abort();
          const controller = this.compactRequest = new AbortController();
          const version = this.compactVersion = (this.compactVersion || 0) + 1;
          this.openedBy = opener;
          opener.setAttribute('aria-disabled', 'true');
          opener.classList.add('loading');
          opener.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
          this.modalContent.innerHTML = '<p class="canvoil-quick-add-state" role="status"></p>';
          this.modalContent.firstElementChild.textContent = window.canvoilStrings?.loadingArtwork || 'Loading artwork…';
          this.modalContent.setAttribute('aria-busy', 'true');
          super.show(opener);
          try {
            const url = new URL(opener.dataset.productUrl, window.location.origin);
            const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
            if (preview) url.searchParams.set('preview_theme_id', preview);
            const html = new DOMParser().parseFromString(await this.fetchCompactHTML(url, controller), 'text/html');
            if (version !== this.compactVersion) return;
            this.productElement = html.querySelector('section[id^="MainProduct-"]');
            if (!this.productElement) throw new Error('Product content unavailable');
            await this.loadCompactDependencies(this.productElement);
            if (version !== this.compactVersion) return;
            const content = this.buildCompactContent(this.productElement, url);
            this.modalContent.replaceChildren(document.importNode(content, true));
            this.bindCompactVariants(url);
            if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
            trapFocus(this, this.querySelector('[role="dialog"]'));
          } catch (error) {
            if (error.name === 'AbortError' || version !== this.compactVersion) return;
            this.showCompactError(opener);
          } finally {
            if (version === this.compactVersion) this.modalContent.removeAttribute('aria-busy');
            if (version === this.compactVersion || opener !== this.openedBy) {
              opener.removeAttribute('aria-disabled');
              opener.classList.remove('loading');
              opener.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
            }
          }
        }

        async fetchCompactHTML(url, controller) {
          let timeout;
          try {
            return await Promise.race([
              fetch(url, { signal: controller.signal }).then(response => {
                if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
                return response.text();
              }),
              new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                  reject(new Error('Product request timed out'));
                  controller.abort();
                }, 15000);
              })
            ]);
          } finally {
            clearTimeout(timeout);
          }
        }

        async loadCompactDependencies(section) {
          const definitions = [['product-form', 'product-form.js'], ['canvoil-price-estimate', 'canvoil-price-estimate.js']];
          await Promise.all(definitions.map(([tag, file]) => {
            if (customElements.get(tag) || !section.querySelector(tag)) return Promise.resolve();
            const source = Array.from(section.querySelectorAll('script[src]')).find(script =>
              new URL(script.src, window.location.href).pathname.endsWith('/' + file));
            if (!source) return Promise.reject(new Error('Product control unavailable'));
            const requests = this.constructor.compactDependencies ||= new Map();
            if (!requests.has(file)) {
              const request = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                const timeout = setTimeout(() => { script.remove(); reject(new Error('Product control timed out')); }, 15000);
                script.src = source.src;
                script.async = true;
                script.onload = () => { clearTimeout(timeout); customElements.get(tag) ? resolve() : reject(new Error('Product control unavailable')); };
                script.onerror = () => { clearTimeout(timeout); script.remove(); reject(new Error('Product control could not be loaded')); };
                document.head.appendChild(script);
              }).catch(error => { requests.delete(file); throw error; });
              requests.set(file, request);
            }
            return requests.get(file);
          }));
        }

        buildCompactContent(section, productURL) {
          const doc = section.ownerDocument;
          const info = section.querySelector('.product__info-container');
          const title = info?.querySelector('.product__title');
          const price = info?.querySelector('[id^="price-"]');
          const form = info?.querySelector('product-form');
          if (!title || !price || !form?.querySelector('form')) throw new Error('Product information incomplete');
          this.sourcePriceId = price.id;
          this.originalSection = section.dataset.section;
          const content = doc.createElement('div');
          content.className = 'canvoil-quick-add';
          const summary = doc.createElement('div');
          summary.className = 'canvoil-quick-add__summary';
          const image = section.querySelector('.product__media img, .product__media-wrapper img');
          if (image) {
            const thumb = doc.createElement('a');
            thumb.className = 'canvoil-quick-add__image';
            thumb.href = productURL.href;
            const img = image.cloneNode(true);
            img.removeAttribute('id');
            img.removeAttribute('style');
            img.sizes = '(max-width: 749px) 88px, 112px';
            img.loading = 'eager';
            thumb.append(img);
            summary.append(thumb);
          }
          const details = doc.createElement('div');
          details.className = 'canvoil-quick-add__details';
          const heading = doc.createElement('h2');
          heading.className = 'canvoil-quick-add__title';
          const titleLink = doc.createElement('a');
          titleLink.href = productURL.href;
          titleLink.textContent = title.textContent.trim();
          heading.append(titleLink);
          details.append(heading);
          price.classList.add('canvoil-quick-add__price');
          details.append(price);
          const review = info.querySelector('.canvoil-product-rating');
          if (review) {
            review.href = productURL.pathname + '#canvoil-product-reviews';
            details.append(review);
          }
          summary.append(details);
          content.append(summary);
          const picker = info.querySelector('variant-selects, variant-radios');
          if (picker) {
            const dropdownPicker = doc.createElement('variant-selects');
            for (const attribute of picker.attributes) dropdownPicker.setAttribute(attribute.name, attribute.value);
            dropdownPicker.className = 'canvoil-quick-add__options';
            dropdownPicker.dataset.updateUrl = 'false';
            const groups = picker.querySelectorAll('[data-option-group], .product-form__input');
            groups.forEach((group, index) => {
              const sourceSelect = group.querySelector('select');
              const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
              if (!sourceSelect && !radios.length) return;
              const name = sourceSelect?.name || radios[0].name;
              const rawLabel = group.querySelector('label, legend')?.childNodes[0]?.textContent.trim();
              const labelText = /frame/i.test(rawLabel || name) ? (window.canvoilStrings?.canvasOrFrame || 'Canvas or frame') : (rawLabel || name.replace(/^options\[|\]$/g, '')).replace(/:$/, '');
              const row = doc.createElement('div');
              row.className = 'canvoil-quick-add__option';
              const select = sourceSelect ? sourceSelect.cloneNode(true) : doc.createElement('select');
              select.id = `CompactOption-${index}`;
              select.name = name;
              select.className = 'canvoil-quick-add__select';
              select.setAttribute('form', form.querySelector('form').id);
              if (!sourceSelect) radios.forEach(radio => {
                const option = doc.createElement('option');
                option.value = radio.value;
                option.textContent = radio.value;
                option.selected = radio.checked;
                select.append(option);
              });
              const label = doc.createElement('label');
              label.htmlFor = select.id;
              label.textContent = labelText + ':';
              row.append(label, select);
              dropdownPicker.append(row);
            });
            const data = picker.querySelector('script[type="application/json"]');
            if (data) dropdownPicker.append(data.cloneNode(true));
            content.append(dropdownPicker);
          }
          form.querySelectorAll('.product-form__quantity, .quick_href, [id^="ProductForm__Inventory"]').forEach(node => node.remove());
          const quantity = doc.createElement('input');
          quantity.type = 'hidden';
          quantity.name = 'quantity';
          quantity.value = '1';
          form.querySelector('form').append(quantity);
          content.append(form);
          // Keep every form association local even when the same product appears twice on a page.
          const namespace = `compact-${this.id}-${this.compactVersion}`;
          const ids = new Map();
          content.querySelectorAll('[id]').forEach(node => { ids.set(node.id, `${namespace}-${node.id}`); node.id = ids.get(node.id); });
          content.querySelectorAll('[for], [form], [aria-labelledby], [aria-describedby], [aria-controls]').forEach(node => {
            ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => {
              if (node.hasAttribute(attribute)) node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
            });
          });
          content.querySelectorAll('script:not([type="application/json"]), style, link').forEach(node => node.remove());
          return content;
        }

        bindCompactVariants(productURL) {
          const picker = this.modalContent.querySelector('variant-selects');
          if (!picker) return;
          // Use Mono's variant data and native product form; scope updates to this popup.
          picker.removeEventListener('change', picker.onVariantChange);
          const variantData = picker.getVariantData();
          const controls = Array.from(picker.querySelectorAll('select'));
          const productForm = this.modalContent.querySelector('product-form');
          const form = productForm.querySelector('form');
          const price = this.modalContent.querySelector('.canvoil-quick-add__price');
          const payment = form.querySelector('.product-form__buttons');
          const image = this.modalContent.querySelector('.canvoil-quick-add__image img');
          let sequence = 0;
          const refresh = async () => {
            const request = ++sequence;
            this.variantRequest?.abort();
            const current = variantData.find(variant => variant.options.every((value, index) => controls[index]?.value === value));
            picker.currentVariant = current;
            productForm.handleErrorMessage();
            this.modalContent.querySelector('[data-compact-price-retry]')?.remove();
            productForm.toggleSubmitButton(true, current ? undefined : window.variantStrings.unavailable);
            if (payment) payment.hidden = true;
            price.setAttribute('aria-busy', 'true');
            price.classList.add('visibility-hidden');
            if (!current) {
              form.querySelector('[name="id"]').value = '';
              price.classList.add('visibility-hidden');
              price.removeAttribute('aria-busy');
              return;
            }
            form.querySelector('[name="id"]').value = current.id;
            form.querySelector('[name="id"]').dispatchEvent(new Event('change', { bubbles: true }));
            form.querySelector('[name="quantity"]').value = current.quantity_rule?.min || 1;
            const featured = current.featured_image || current.featured_media?.preview_image;
            if (image && featured?.src) {
              image.src = featured.src;
              image.removeAttribute('srcset');
              if (featured.alt) image.alt = featured.alt;
            }
            const controller = this.variantRequest = new AbortController();
            const url = new URL(productURL.href);
            url.searchParams.set('variant', current.id);
            url.searchParams.set('section_id', this.originalSection);
            try {
              const html = new DOMParser().parseFromString(await this.fetchCompactHTML(url, controller), 'text/html');
              if (request !== sequence || controller.signal.aborted || !picker.isConnected) return;
              const updated = html.getElementById(this.sourcePriceId);
              if (!updated) throw new Error('Price unavailable');
              price.innerHTML = updated.innerHTML;
              price.classList.remove('visibility-hidden');
              productForm.toggleSubmitButton(!current.available, window.variantStrings.soldOut);
              if (payment) payment.hidden = !current.available;
            } catch (error) {
              if (error.name === 'AbortError' || request !== sequence || !picker.isConnected) return;
              price.classList.add('visibility-hidden');
              const retry = document.createElement('div');
              retry.dataset.compactPriceRetry = '';
              retry.className = 'canvoil-quick-add-state';
              retry.setAttribute('role', 'alert');
              const message = document.createElement('p');
              message.textContent = window.canvoilStrings?.variantPriceError || 'We couldn’t update the price for this option. Please try again.';
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'button button--primary-outline';
              button.textContent = window.canvoilStrings?.variantPriceRetry || 'Try again';
              button.addEventListener('click', refresh);
              retry.append(message, button);
              picker.after(retry);
            } finally {
              if (request === sequence) price.removeAttribute('aria-busy');
            }
          };
          picker.addEventListener('change', refresh);
          const selected = variantData.find(variant => String(variant.id) === form.querySelector('[name="id"]').value);
          if (selected) form.querySelector('[name="quantity"]').value = selected.quantity_rule?.min || 1;
          if (payment) payment.hidden = !selected?.available;
        }

        showCompactError(opener) {
          this.modalContent.innerHTML = '<div class="canvoil-quick-add-state" role="alert"><p></p><button type="button" class="button"></button><a></a></div>';
          this.modalContent.querySelector('p').textContent = window.canvoilStrings?.artworkLoadError || 'We couldn’t load this artwork. Please try again.';
          this.modalContent.querySelector('button').textContent = window.canvoilStrings?.tryAgain || 'Try again';
          this.modalContent.querySelector('a').textContent = window.canvoilStrings?.viewArtwork || 'View artwork';
          this.modalContent.querySelector('button').addEventListener('click', () => this.showCompact(opener));
          this.modalContent.querySelector('a').href = opener.dataset.productUrl;
          trapFocus(this, this.querySelector('[role="dialog"]'));
        }

        setInnerHTML(element, html) {
          element.innerHTML = html;
  
          // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
          element.querySelectorAll('script').forEach((oldScriptTag) => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach((attribute) => {
              newScriptTag.setAttribute(attribute.name, attribute.value);
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
          });
        }
  
        preventVariantURLSwitching() {
          const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
          if (!variantPicker) return;
  
          variantPicker.setAttribute('data-update-url', 'false');
        }
  
        removeDOMElements() {
          const pickupAvailability = this.productElement.querySelector('pickup-availability');
          if (pickupAvailability) pickupAvailability.remove();
  
          const productModal = this.productElement.querySelector('product-modal');
          if (productModal) productModal.remove();

          const breadcrumb = this.productElement.querySelector('.breadcrumb');
          if (breadcrumb) breadcrumb.remove();
  
          const modalDialog = this.productElement.querySelectorAll('modal-dialog');
          if (modalDialog) modalDialog.forEach((modal) => modal.remove());
        }
  
        preventDuplicatedIDs() {
          const sectionId = this.productElement.dataset.section;
          this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
          this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
            element.dataset.originalSection = sectionId;
          });
        }
  
        removeGalleryListSemantic() {
          const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
          if (!galleryList) return;
  
          galleryList.setAttribute('role', 'presentation');
          galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
        }
  
        updateImageSizes() {
          const product = this.modalContent.querySelector('.product');
          const desktopColumns = product.classList.contains('product--columns');
          if (!desktopColumns) return;
  
          const mediaImages = product.querySelectorAll('.product__media img');
          if (!mediaImages.length) return;
  
          let mediaImageSizes =
            '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
  
          if (product.classList.contains('product--medium')) {
            mediaImageSizes = mediaImageSizes.replace('715px', '605px');
          } else if (product.classList.contains('product--small')) {
            mediaImageSizes = mediaImageSizes.replace('715px', '495px');
          }
  
          mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
        }
      }
    );
  }
  
